/**
 * Zoom Meeting SDK JWT — prefer Medplum `zoom-signature` Bot; optional dev-only HS256 fallback.
 */
import type { MedplumClient } from '@medplum/core';
import { SignJWT } from 'jose';
import { config } from '@/config';
import { fetchZoomSignatureFromBot } from './zoomSignatureService';

function parseMeetingNumber(meetingNumber: string): number {
  const mn = Number.parseInt(meetingNumber.replace(/\s/g, ''), 10);
  if (Number.isNaN(mn)) {
    throw new Error('Invalid meetingNumber');
  }
  return mn;
}

async function signZoomJwtDev(
  sdkKey: string,
  sdkSecret: string,
  meetingNumber: string,
  role: number,
): Promise<string> {
  const iat = Math.floor(Date.now() / 1000) - 30;
  const exp = iat + 60 * 60 * 2;
  const mn = parseMeetingNumber(meetingNumber);
  const secret = new TextEncoder().encode(sdkSecret);
  return new SignJWT({
    appKey: sdkKey,
    sdkKey,
    mn,
    role,
    tokenExp: exp,
  })
    .setProtectedHeader({ alg: 'HS256', typ: 'JWT' })
    .setIssuedAt(iat)
    .setExpirationTime(exp)
    .sign(secret);
}

/**
 * Obtain a Zoom Meeting SDK signature.
 * When `medplum` is set, calls the Zoom Signature Bot first (project secrets).
 * In development, falls back to client-side HS256 if the bot fails and `VITE_ZOOM_SDK_SECRET` is set.
 */
export async function getZoomSignature(
  medplum: MedplumClient | undefined,
  meetingNumber: string,
  role: number,
): Promise<string> {
  const { sdkKey, sdkSecret } = config.zoom;

  if (medplum) {
    try {
      return await fetchZoomSignatureFromBot(medplum, meetingNumber, role);
    } catch (err) {
      const fallback =
        config.isDevelopment && sdkSecret && sdkKey
          ? await signZoomJwtDev(sdkKey, sdkSecret, meetingNumber, role)
          : undefined;
      if (fallback) {
        console.warn('[Zoom] Medplum signature bot failed; using dev client-side signature:', err);
        return fallback;
      }
      throw err instanceof Error
        ? err
        : new Error('Zoom signature bot failed and no VITE_ZOOM_SDK_SECRET for dev fallback');
    }
  }

  if (config.isDevelopment && sdkKey && sdkSecret) {
    return signZoomJwtDev(sdkKey, sdkSecret, meetingNumber, role);
  }

  throw new Error(
    'Configure the Zoom Signature Bot (project secrets ZOOM_SDK_KEY / ZOOM_SDK_SECRET) ' +
      'or set VITE_ZOOM_SDK_SECRET for local dev-only signing.',
  );
}
